import React, { useCallback, useEffect, useState } from "react";
import AutocompleteInput from "../../common/AutocompleteInput";
import CreateTeamButton from "./CreateTeamButton";
import DonorList from "../general/DonorList";
import { isMobileView, isTabletView, pluralizeText } from "../../../lib/utils";

export default function TeamsSection(props) {
  const {
    currencyCode,
    donorListDisplay,
    donorsCount,
    donorSiteURL,
    isDonorListPublic,
    newDonorsCount,
    onCreateTeam,
    raffleEnrollment,
    team,
    teamPageURL,
    teams,
    setDonorsCount,
    setNewDonorsCount,
    systemLists: { countries },
  } = props;
  const { allowPublicCreateReferrer, sellerEnrollmentID } =
    raffleEnrollment || {};

  const [showMore, setShowMore] = useState(false);
  const [displayTeams, setDisplayTeams] = useState([]);
  const [searchedTeam, setSearchedTeam] = useState(null);
  const [sortBy, setSortBy] = useState("name");

  const isMobile = isMobileView();
  const displayLimit = isMobile || isTabletView() ? 4 : 6;

  useEffect(() => {
    sortTeams();
    //eslint-disable-next-line
  }, [teams]);

  const sortOptions = [
    {
      name: "Team Name",
      value: "name",
    },
    {
      name: "Most Raised",
      value: "most",
    },
    {
      name: "Least Raised",
      value: "least",
    },
    {
      name: "Nearest to Goal",
      value: "nearest",
    },
  ];

  const getTeamURL = useCallback(
    (teamName) => {
      const teamSlug = teams.find(
        (t) => t.name.toLowerCase() === teamName?.toLowerCase()
      )?.slug;
      return donorSiteURL + "/" + teamSlug;
    },
    [donorSiteURL, teams]
  );

  const sortTeams = (_sortBy = sortBy, _showMore = showMore) => {
    let _displayTeams = teams;

    switch (_sortBy) {
      case "most":
        _displayTeams.sort((a, b) => (a.ticketsSold < b.ticketsSold ? 1 : -1));
        break;
      case "least":
        _displayTeams.sort((a, b) => (a.ticketsSold > b.ticketsSold ? 1 : -1));
        break;
      case "nearest":
        _displayTeams.sort((a, b) => {
          if (a.ticketsSold === 0 && b.ticketsSold === 0) {
            return a.goal > b.goal ? 1 : -1;
          }
          return progress(a) < progress(b) ? 1 : -1;
        });
        break;
      default: //sort by name
        _displayTeams.sort((a, b) =>
          a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
        );
        break;
    }

    if (!_showMore) {
      _displayTeams = _displayTeams.slice(0, displayLimit);
    }

    setSortBy(_sortBy);
    setDisplayTeams(_displayTeams);
  };

  const progress = (team) => {
    if (!team.goal) return 0;
    if (team.ticketsSold >= team.goal) return 100;
    return (team.ticketsSold / team.goal) * 100;
  };

  const TeamsSearch = () => {
    const searchTeams = (searchValue) => {
      const _teams = teams
        .filter((team) => teamMatchIndex(team.name, searchValue) >= 0)
        .map((t) => {
          return { ...t, value: t.name, key: t.id };
        })
        .sort(
          (s1, s2) =>
            teamMatchIndex(s1.name, searchValue) -
            teamMatchIndex(s2.name, searchValue)
        );
      return _teams;
    };

    const teamMatchIndex = (teamName, searchValue) => {
      return teamName
        .toLowerCase()
        .trim()
        .indexOf(searchValue.toLowerCase().trim());
    };

    const selectSearchTeam = (searchResult) => {
      setSearchedTeam(searchResult);
      setDisplayTeams([searchResult]);
    };

    return (
      <div className="teams-search-input">
        <div className="flex flex-align-center teams-search">
          <i className="material-icons mr-8">search</i>
          {!searchedTeam ? (
            <AutocompleteInput
              getSearchResults={searchTeams}
              selectSearchResult={selectSearchTeam}
              placeholder={"Search teams"}
            />
          ) : (
            <div>
              <p className="accent-text">{searchedTeam.name}</p>
              <span
                className="clear-select input"
                onClick={() => {
                  setSearchedTeam(null);
                  sortTeams();
                }}
              >
                x
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const Teams = () => {
    return (
      <>
        <div className="teams-grid">
          {displayTeams.map((team, index) => (
            <div className="team-card" key={index}>
              <p className="fs-18 fw-600 mb-8">{team.name}</p>
              {!!team.goal && (
                <div className="percentage-bar team-percentage-bar relative mb-8">
                  <div className="bar" />
                  <div
                    className={`filled-in ${
                      progress(team) <= 92 ? "not-complete" : ""
                    }`}
                    style={{ width: `${progress(team)}%` }}
                  />
                </div>
              )}
              <div className="flex flex-justify-space flex-align-center mb-4">
                <p className="fs-14 fw-600">
                  {team.ticketsSold} {pluralizeText("ticket", team.ticketsSold)}
                </p>
                {!!team.goal && (
                  <p className="fs-12 accent-text">Goal: {team.goal} tickets</p>
                )}
              </div>
              <p className="fs-12 accent-text">
                {team.numberOfDonors}{" "}
                {pluralizeText("Donor", team.numberOfDonors)}
              </p>
              <a
                href={teamPageURL(team.slug)}
                className="btn btn-squared mt-16"
              >
                Visit team page
              </a>
            </div>
          ))}
        </div>

        {!searchedTeam && teams.length > displayLimit && (
          <div
            className="flex flex-justify-center flex-align-center uppercase-text mt-48 brand-color-text pointer"
            onClick={() => {
              setShowMore(!showMore);
              sortTeams(sortBy, !showMore); //send in showMore as state doesn't update fast enough
            }}
          >
            <p className="fw-700" style={{ letterSpacing: 2 }}>
              Show {showMore ? "less" : "more"}
            </p>
          </div>
        )}
      </>
    );
  };

  return (
    (isDonorListPublic ||
      (!team && (allowPublicCreateReferrer || !!teams.length))) && (
      <div
        className="teams-container"
        style={{
          //Setting display logic here (rather than not returning at all) because we need to return DonorList in order to get donorCount
          display:
            !donorsCount &&
            (team || (!allowPublicCreateReferrer && !teams.length))
              ? "none"
              : "",
        }}
      >
        <p className="background-text mobile-hidden">
          Thank <br /> you!
        </p>
        <div
          className="container flex flex-justify-space"
          style={{ zIndex: 99 }}
        >
          {team ? (
            <DonorList
              currencyCode={currencyCode}
              donorListDisplay={donorListDisplay}
              donorsCount={donorsCount}
              fullWidthDisplay
              newDonorsCount={newDonorsCount}
              sellerEnrollmentId={sellerEnrollmentID}
              setDonorsCount={setDonorsCount}
              setNewDonorsCount={setNewDonorsCount}
              team={team}
              teamURL={getTeamURL}
            />
          ) : (
            <>
              {(allowPublicCreateReferrer || !!teams.length) && (
                <div
                  className={
                    isDonorListPublic ? "teams-container-left" : "full-width"
                  }
                >
                  <div
                    className={`teams-container-header${
                      !teams.length ? " no-teams" : ""
                    }`}
                  >
                    <div className="flex flex-align-center flex-justify-space">
                      <p className="fs-28 fw-600 brand-color-text flex flex-align-center mobile-mb-8">
                        Teams
                        <span
                          className="fs-18 fw-400 ml-8"
                          style={{ color: "#828282" }}
                        >
                          ({teams.length})
                        </span>
                      </p>
                      {isMobile && allowPublicCreateReferrer && (
                        <CreateTeamButton
                          countries={countries}
                          onCreateTeam={onCreateTeam}
                          sellerEnrollmentId={sellerEnrollmentID}
                          teams={teams}
                        />
                      )}
                    </div>
                    {!!teams.length && <TeamsSearch />}
                    {!isMobile && allowPublicCreateReferrer && (
                      <CreateTeamButton
                        countries={countries}
                        onCreateTeam={onCreateTeam}
                        sellerEnrollmentId={sellerEnrollmentID}
                        teams={teams}
                      />
                    )}
                  </div>
                  {!!teams.length && (
                    <div className="flex flex-align-center mb-24">
                      <p className="accent-text small-text">Sort by</p>
                      <select
                        className="teams-sort-by-select"
                        onChange={(event) => sortTeams(event.target.value)}
                        value={sortBy}
                        disabled={!!searchedTeam}
                      >
                        {sortOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <Teams />
                </div>
              )}
              {isDonorListPublic && (
                <DonorList
                  currencyCode={currencyCode}
                  donorListDisplay={donorListDisplay}
                  donorsCount={donorsCount}
                  fullWidthDisplay={!(teams.length > 0)}
                  newDonorsCount={newDonorsCount}
                  sellerEnrollmentId={sellerEnrollmentID}
                  setDonorsCount={setDonorsCount}
                  setNewDonorsCount={setNewDonorsCount}
                  team={team}
                  teamURL={getTeamURL}
                />
              )}
            </>
          )}
        </div>
      </div>
    )
  );
}
